// Sprint CAL-1b — scheda «To do List» nella colonna del calendario (rotte e chiusura di CAL-1a)
import React, { useCallback, useEffect, useState } from "react";
import { GripVertical, Plus, Check, ArrowUp, ArrowDown } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { SchedaRichiudibile } from "./SchedaRichiudibile";
import { ChiudiAttivita, Rimanda } from "./ChiusuraAttivita";
import { coloreEvento, fmtEvento, leggiLS, scriviLS, scadenzaTodo, ordinaTodo } from "./calendarioComuni";

const LS_PILLOLE = "cal-todo-pillole", LS_FATTE = "cal-todo-fatte", LS_MODO = "cal-todo-modo", LS_ORDINE = "cal-todo-ordine";

// Mie = le proprie; Sede = le voci delle sedi scelte in «Calendari da mostrare»
export function useTodo(scelti) {
  const [pillole, setPilloleS] = useState(() => leggiLS(LS_PILLOLE, ["mie", "sede"]));
  const [fatte, setFatteS] = useState(() => !!leggiLS(LS_FATTE, false));
  const [voci, setVoci] = useState([]);
  const calendari = [pillole.includes("mie") && "mio", ...(pillole.includes("sede") ? scelti.filter((s) => s.startsWith("sede:")) : [])].filter(Boolean).join(",");
  const carica = useCallback(() => {
    if (!calendari) { setVoci([]); return; }
    api.get(`/calendario/todo?${new URLSearchParams({ calendari, fatte: String(fatte) })}`).then((r) => setVoci(r.data || [])).catch(() => setVoci([]));
  }, [calendari, fatte]);
  useEffect(() => { carica(); }, [carica]);
  const setPillole = (p) => { if (!p.length) return; setPilloleS(p); scriviLS(LS_PILLOLE, p); };
  const setFatte = (v) => { setFatteS(v); scriviLS(LS_FATTE, v); };
  return { voci, carica, pillole, setPillole, fatte, setFatte, urgenti: voci.filter((v) => scadenzaTodo(v)).length };
}

function Pillola({ attiva, onClick, testid, children, disabled }) {
  return <button type="button" onClick={onClick} disabled={disabled} aria-pressed={attiva} data-testid={testid}
    className={`rounded-full border px-2.5 py-0.5 text-xs transition-colors ${attiva ? "bg-[#0F4C81] text-white border-[#0F4C81]" : "bg-white text-slate-600 hover:bg-slate-50"}`}>{children}</button>;
}

function Voce({ v, manuale, largo, onApri, onChiudi, onRicarica, sposta, trascina }) {
  const sc = scadenzaTodo(v);
  return (
    <div className={`flex items-start gap-1 rounded border px-2 py-1.5 text-sm ${v.fatto ? "bg-slate-50 text-slate-400" : sc === "scaduta" ? "border-rose-300 bg-rose-50" : sc === "oggi" ? "border-amber-300 bg-amber-50" : "bg-white"}`}
      data-testid={`cal-todo-voce-${v.id}`} onDragOver={(e) => manuale && e.preventDefault()} onDrop={(e) => trascina(e, v.id)}>
      {manuale && largo && !v.fatto && <span draggable onDragStart={(e) => e.dataTransfer.setData("text/plain", v.id)} className="cursor-grab pt-0.5 text-slate-400"
        title="Trascina per riordinare" data-testid={`cal-todo-maniglia-${v.id}`}><GripVertical className="h-4 w-4" /></span>}
      <span className="mt-1.5 h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: coloreEvento(v) }} />
      <button type="button" onClick={() => onApri(v)} className="flex-1 min-w-0 text-left" data-testid={`cal-todo-apri-${v.id}`}>
        <span className={`block truncate font-medium ${v.fatto ? "line-through" : ""}`}>{v.titolo}</span>
        {(v.fatto ? v.esito : v.descrizione) && <span className="block truncate text-xs text-slate-500" data-testid={`cal-todo-riga2-${v.id}`}>{v.fatto ? `Nota: ${v.esito}` : v.descrizione}</span>}
        <span className="block truncate text-xs text-slate-500" data-testid={`cal-todo-riga3-${v.id}`}>
          {fmtEvento(v)} · {v.calendario === "sede" ? `${v.sede_nome || "Sede"}${v.proprietario_nome ? ` · ${v.proprietario_nome}` : ""}` : "Mie"}</span>
      </button>
      {/* Sprint CAL-1c — chi può chiudere (anche l'assegnato della voce di sede); Rimanda come icona: la terza riga resta intera */}
      {!v.fatto && v.puo_chiudere && <div className="flex flex-col items-end gap-1 shrink-0">
        <button type="button" onClick={() => onChiudi(v)} title="Segna fatto" className="rounded border p-0.5 text-emerald-700 hover:bg-emerald-50"
          data-testid={`cal-todo-fatto-${v.id}`}><Check className="h-3.5 w-3.5" /></button>
        {sc && <Rimanda ev={v} onFatto={onRicarica} testid={`cal-todo-rimanda-${v.id}`} icona />}
      </div>}
      {manuale && !largo && !v.fatto && <div className="flex flex-col shrink-0">
        <button type="button" onClick={() => sposta(v.id, -1)} aria-label="Su" className="p-0.5 text-slate-500" data-testid={`cal-todo-su-${v.id}`}><ArrowUp className="h-3.5 w-3.5" /></button>
        <button type="button" onClick={() => sposta(v.id, 1)} aria-label="Giù" className="p-0.5 text-slate-500" data-testid={`cal-todo-giu-${v.id}`}><ArrowDown className="h-3.5 w-3.5" /></button>
      </div>}
    </div>
  );
}

export function TodoScheda({ todo, largo, onApri, onNuova }) {
  const [modo, setModoS] = useState(() => leggiLS(LS_MODO, "manuale"));
  const [ordine, setOrdineS] = useState(() => leggiLS(LS_ORDINE, []));
  const [chiudi, setChiudi] = useState(null);
  const setModo = (m) => { setModoS(m); scriviLS(LS_MODO, m); };
  const lista = ordinaTodo(todo.voci, modo, ordine);
  const aperte = lista.filter((v) => !v.fatto);
  const salvaOrdine = (ids) => { setOrdineS(ids); scriviLS(LS_ORDINE, ids); };
  const sposta = (id, dir) => {
    const ids = aperte.map((v) => v.id), i = ids.indexOf(id), j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]]; salvaOrdine(ids);
  };
  const trascina = (e, su) => {
    const id = e.dataTransfer?.getData("text/plain");
    if (!id || id === su || modo !== "manuale") return;
    const ids = aperte.map((v) => v.id).filter((x) => x !== id);
    ids.splice(ids.indexOf(su), 0, id); salvaOrdine(ids);
  };
  const p = todo.pillole;
  const pillola = (k) => todo.setPillole(p.includes(k) ? p.filter((x) => x !== k) : [...p, k]);
  return (
    <SchedaRichiudibile chiave="cal-scheda-todo" titolo={`To do List (${aperte.length})`} testid="cal-scheda-todo"
      azioni={<Button size="sm" variant="outline" className="h-7 px-2" onClick={onNuova} aria-label="Nuova voce" data-testid="cal-todo-nuova"><Plus className="h-4 w-4" /></Button>}>
      <div className="flex flex-wrap items-center gap-1">
        <Pillola attiva={p.includes("mie")} onClick={() => pillola("mie")} testid="cal-todo-pillola-mie">Mie</Pillola>
        <Pillola attiva={p.includes("sede")} onClick={() => pillola("sede")} testid="cal-todo-pillola-sede">Sede</Pillola>
        <span className="ml-auto flex gap-1">
          <Pillola attiva={modo === "manuale"} onClick={() => setModo("manuale")} testid="cal-todo-ordine-manuale">Mio ordine</Pillola>
          <Pillola attiva={modo === "scadenza"} onClick={() => setModo("scadenza")} testid="cal-todo-ordine-scadenza">Scadenza</Pillola></span>
      </div>
      <label className="flex items-center gap-2 text-xs text-slate-600"><input type="checkbox" checked={todo.fatte} onChange={(e) => todo.setFatte(e.target.checked)}
        data-testid="cal-todo-mostra-fatte" /> Mostra le fatte (ultimi 30 giorni)</label>
      <div className="space-y-1 max-h-[50vh] overflow-y-auto" data-testid="cal-todo-lista">
        {!lista.length && <div className="text-xs text-slate-400 py-2">Nessuna voce</div>}
        {lista.map((v) => <Voce key={v.id} v={v} manuale={modo === "manuale"} largo={largo} onApri={onApri} onChiudi={setChiudi}
          onRicarica={todo.carica} sposta={sposta} trascina={trascina} />)}
      </div>
      {chiudi && <ChiudiAttivita ev={chiudi} onChiudi={() => setChiudi(null)} onFatto={() => { setChiudi(null); todo.carica(); }} />}
    </SchedaRichiudibile>
  );
}
